import type { FilterCollection, FilterName } from '@/stores/filter.store';

// to know when a dropdown group is active to signalize if any filter is active
export const FilterValues: Record<string, (keyof FilterCollection)[]> = {
	FilterReleaseYear: ['release_year_from', 'release_year_until'],
	FilterBarGenres: ['genres', 'exclude_genres'],
	FilterPrice: ['max_price', 'min_price'],
	FilterMonetizationTypes: ['monetization_types'],
	FilterPresentationTypes: ['presentation_types'],
	FilterRating: ['scoring_filter_types'],
	FilterAgeCertification: ['age_certifications'],
	FilterProviders: ['providers'],
	FilterProductionCountry: ['production_countries', 'exclude_production_countries'],
	FilterRuntime: ['min_runtime', 'max_runtime'],
	// SUB-GENRES
	FilterSubGenres: ['subgenres'],
	// SUB-GENRES
};

const isFilterActive = (filterName: FilterName, value: any = undefined, currentFilters: FilterCollection): boolean => {
	const currentFilterValue = currentFilters[filterName];

	// when a value was passed check if that value is part of the filter
	// value’s items or when it’s no array filter value compare directly
	if (value !== undefined) {
		if (Array.isArray(currentFilterValue)) {
			// some filters are considered to be "active" when their value
			// contains no elements
			switch (filterName) {
				case 'age_certifications':
				case 'genres':
				case 'exclude_genres':
				case 'monetization_types':
				case 'presentation_types':
					if (currentFilterValue.length === 0) {
						return false;
					}
					break;
				case 'providers':
					if (currentFilterValue.length === 0) {
						return true;
					}
			}
			return (currentFilterValue as any[]).includes(value);
		}
		return currentFilterValue === value;
	}

	// no value was passed for comparison, consider anything in array values
	// as "active" or return the current filter value
	if (Array.isArray(currentFilterValue)) {
		return currentFilterValue.length > 0;
	} else {
		return currentFilterValue as boolean;
	}
};

const getActiveFilters = (filterNames: FilterName[], currentFilters: FilterCollection): Partial<FilterCollection> => {
	return filterNames
		.filter(filterName => isFilterActive(filterName, undefined, currentFilters))
		.reduce((root, filterName) => {
			root[filterName] = currentFilters[filterName];
			return root;
		}, {} as any);
};

const getUsedFilters = (
	filterValues: Partial<typeof FilterValues> = FilterValues,
	filterComponents?: any[]
): FilterName[] => {
	return (
		Object.keys(filterValues)
			// either have filterComponents and filter by them or just take in all filterValues
			.filter(key =>
				filterComponents ? filterComponents!.map(f => f.extendOptions?.name).indexOf(key) !== -1 : true
			)
			.flatMap(key => (filterValues as any)[key])
	);
};

const isAnyFilterActive = (filterNames: FilterName[], currentFilters: FilterCollection): boolean => {
	return filterNames.some(filterName => {
		return isFilterActive(filterName, undefined, currentFilters);
	});
};

// check if there are any filters active that cause query parameters.
// e.g. 1x provider selected wouldn't create a query parameter.
const hasQueryParamFilters = (currentFilters: FilterCollection) => {
	const activeFilters = getActiveFilters(getUsedFilters(), currentFilters);
	const { providers = [], ...restActives } = activeFilters;
	return providers.length > 1 || Object.keys(restActives).length > 0;
};

export { getActiveFilters, getUsedFilters, isAnyFilterActive, isFilterActive, hasQueryParamFilters };
